import { isInFlight } from 'modules/export/utils/export-status.utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import InventoryDownloadService from 'modules/export/services/inventory/inventory-download.service';
import { BackgroundExportItem } from 'modules/export/storage/background-export-queue.storage';

export const useBackgroundExportPolling = (
  backgroundQueue: BackgroundExportItem[],
  updateBackgroundQueue: (queue: BackgroundExportItem[]) => void,
  onExportCompleted?: (exportId: string, exportData: any) => void,
  modalState?: {
    isInfoModalOpen: boolean;
    isDownloadModalOpen: boolean;
  },
) => {
  const { t } = useTranslation('notification');
  const previousPendingIdsRef = useRef<Set<string>>(new Set());

  // Generate generic file name based on export ID
  const generateGenericFileName = (exportId: string): string => {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `export_${timestamp}_${exportId.slice(-8)}`;
  };

  const checkDownloadStatus = useCallback(async () => {
    const currentQueue = backgroundQueue || [];
    // Incluye SENT e IN_PROGRESS: el worker los produce mientras trabaja, y
    // si el polling solo mirase los PENDING abandonaria el export en cuanto
    // llegase el primer aviso de progreso.
    const pendingItems = currentQueue.filter((item) => isInFlight(item.status));

    if (pendingItems.length === 0) {
      return currentQueue;
    }

    const pendingIds = pendingItems.map((item) => item._id);

    const response = await InventoryDownloadService.checkDownloadStatus(pendingIds);

    const statusMap = new Map(response.data?.map((item) => [item._id, item.status]) || []);
    const responseDataMap = new Map(response.data?.map((item) => [item._id, item]) || []);

    let queueChanged = false;
    const updatedQueue = currentQueue.filter((item) => {
      if (item.status === 'DONE') {
        queueChanged = true;
        return false;
      }
      const serverStatus = statusMap.get(item._id);

      // Handle orphaned items: if item is not in API response, remove immediately
      if (serverStatus === undefined) {
        queueChanged = true;
        return false; // Remove orphaned item immediately
      }

      if (serverStatus === 'DONE') {
        // Dispara el callback al pasar de cualquier estado en vuelo a DONE
        if (isInFlight(item.status)) {
          const exportName: string = item.name || generateGenericFileName(item._id);
          const exportId: string = item._id;

          // const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
          // const relativePath = `/ms-inventory/api/download/download/${exportId}`;
          // const downloadUrl = `${baseUrl}${relativePath}`;

          // Check if any modal is open - if so, don't show toast notification
          const isAnyModalOpen = modalState?.isInfoModalOpen || modalState?.isDownloadModalOpen;
          if (!isAnyModalOpen) {
            toast(
              () => {
                return (
                  <Box
                    sx={{
                      p: 0,
                      minWidth: 300,
                      maxWidth: 400,
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      toast.dismiss(exportId);
                    }}
                  >
                    <Stack direction='row' spacing={2} alignItems='flex-start'>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'success.main',
                          color: 'success.contrastText',
                        }}
                      >
                        <CheckCircleIcon />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant='subtitle2' fontWeight={600} color='success.main'>
                            {t('notification:titles.EXPORT_DONE', { defaultValue: 'Exportación Completada' })}
                          </Typography>
                        </Stack>

                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                          {t('notification:messages.EXPORT_DONE_MESSAGE', {
                            defaultValue: 'La exportación "{{name}}" se ha completado exitosamente.',
                            name: exportName,
                          })}
                        </Typography>

                        <Typography variant='caption' color='text.secondary'>
                          Listo para descargar
                        </Typography>
                      </Box>

                      <IconButton
                        size='small'
                        onClick={() => {
                          toast.dismiss(exportId);
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              },
              {
                id: exportId,
                duration: 6000,
                position: 'top-right',
              },
            );
          }
        }

        // Notify callback about completed export
        if (onExportCompleted) {
          const exportData = responseDataMap.get(item?._id);

          onExportCompleted(item._id, exportData);
        }

        queueChanged = true;
        return false;
      }

      // Handle ERROR status - remove from queue and show notification
      if (serverStatus === 'ERROR') {
        // Dispara el callback al pasar de cualquier estado en vuelo a ERROR
        if (isInFlight(item.status)) {
          const exportName: string = item.name || generateGenericFileName(item._id);
          const exportId: string = item._id;

          // Check if any modal is open - if so, don't show toast notification
          const isAnyModalOpen = modalState?.isInfoModalOpen || modalState?.isDownloadModalOpen;
          if (!isAnyModalOpen) {
            toast(
              () => {
                return (
                  <Box
                    sx={{
                      p: 0,
                      minWidth: 300,
                      maxWidth: 400,
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      toast.dismiss(exportId);
                    }}
                  >
                    <Stack direction='row' spacing={2} alignItems='flex-start'>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'error.main',
                          color: 'error.contrastText',
                        }}
                      >
                        <ErrorIcon />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant='subtitle2' fontWeight={600} color='error.main'>
                            {t('notification:titles.EXPORT_FAILED', { defaultValue: 'Exportación Fallida' })}
                          </Typography>
                        </Stack>

                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                          {t('notification:messages.EXPORT_FAILED_MESSAGE', {
                            defaultValue: 'La exportación "{{name}}" ha fallado y ha sido eliminada de la cola.',
                            name: exportName,
                          })}
                        </Typography>
                      </Box>

                      <IconButton
                        size='small'
                        onClick={() => {
                          toast.dismiss(exportId);
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              },
              {
                id: exportId,
                duration: 8000,
                position: 'top-right',
              },
            );
          }
        }

        // Notify callback about failed export
        if (onExportCompleted) {
          const exportData = responseDataMap.get(item._id);
          onExportCompleted(item._id, exportData);
        }

        queueChanged = true;
        return false; // Remove from queue
      }
      return true;
    });

    const stillPendingIds = new Set(
      response.data?.filter((item) => item.status !== 'DONE' && item.status !== 'ERROR').map((item) => item._id) || [],
    );

    if (queueChanged) {
      updateBackgroundQueue(updatedQueue);
    }

    previousPendingIdsRef.current = stillPendingIds;
    return updatedQueue;
  }, [backgroundQueue, onExportCompleted, modalState, t, updateBackgroundQueue]);

  const { data: queue = backgroundQueue } = useQuery({
    queryKey: ['background-export-status', backgroundQueue],
    queryFn: checkDownloadStatus,
    refetchInterval: (data) => {
      const pendingItems = data?.filter((item) => item?.status === 'PENDING') || [];
      return pendingItems.length > 0 ? 5000 : false;
    },
    enabled: backgroundQueue.length > 0,
  });

  return { queue };
};
